import React from 'react';
import { Checkbox, InputBase } from '@mui/material';
import { withStyles } from '@mui/styles';
import Select from 'react-select';
import { indexOf, filter, map, range } from 'lodash';
import classnames from 'classnames';
import UIUtils from '../../lib/UIUtils';
import { getLocalTimeZoneLabel } from '../../lib/DateUtils';
import TimeSelector from './TimeSelector';
import DateTimeField from './DateTimeField';
import {
  SelectInput,
  SelectOption,
  baseSelectStyles,
  getCurrentSelectedOption,
  getNewSelectionValue,
} from './rselect/ReactSelectUtils';

const FREQUENCY_INTERVALS = [
  {
    label: 'Once',
    value: 'Once',
  },
  {
    label: 'Every 15 Minutes',
    value: '15 Minutes',
  },
  {
    label: 'Every 30 Minutes',
    value: '30 Minutes',
  },
  {
    label: 'Every Hour',
    value: 'Hour',
  },
  {
    label: 'Every N Hours',
    value: 'N Hours',
  },
  {
    label: 'Every Day',
    value: 'Day',
  },
  {
    label: 'Every N Days',
    value: 'N Days',
  },
  {
    label: 'Every Week',
    value: 'Week',
  },
  {
    label: 'Every Month',
    value: 'Month',
  },
  {
    label: 'Every N Months',
    value: 'N Months',
  },
];

const N_HOURS_OPTIONS = map(range(2, 24), (value) => ({
  label: value,
  value: String(value),
}));

const N_DAYS_OPTIONS = map(range(2, 31), (value) => ({
  label: value,
  value: String(value),
}));

const N_MONTHS_OPTIONS = map(range(2, 12), (value) => ({
  label: value,
  value: String(value),
}));

const muiStyles = {
  checkbox: {
    root: {
      paddingLeft: 4,
      paddingRight: 4,
    },
  },
};

const StyledInputBase = withStyles((theme) => ({
  root: {
    'label + &': {
      marginBottom: 9,
    },
  },
  input: {
    position: 'relative',
    backgroundColor: theme?.palette?.common?.white,
    fontWeight: 400,
    padding: 6,
    color: '#273438',
    borderRadius: 2,
    height: 28,
    border: '1px solid #ccc',
    fontSize: 12,
  },
}))(InputBase);

const StyledCheckbox = withStyles(muiStyles.checkbox)(Checkbox);

class FrequencyField extends React.Component {
  constructor(props) {
    super(props);

    const currentTimeState = this._getStateFromCurrentTime('Day');

    this.state = {
      ...currentTimeState,
      isTimeSpecified: false,
    };

    this._renderSelectField = this._renderSelectField.bind(this);
    this._handleInternalValueChanged = this._handleInternalValueChanged.bind(this);
    this._handleTimestampChanged = this._handleTimestampChanged.bind(this);
    this._handleSetTimeCheckboxChanged = this._handleSetTimeCheckboxChanged.bind(this);
    this._updateValueFromState = this._updateValueFromState.bind(this);
    this._updateStateFromProps = this._updateStateFromProps.bind(this);
    this._getStateFromCurrentTime = this._getStateFromCurrentTime.bind(this);
  }

  componentDidMount() {
    if (
      this.props.value &&
      this.props.value !== '0 0/1 * 1/1 * ? *' /* if cron is "Continuous", fall back to default */
    ) {
      this.setState({ isTimeSpecified: true });
      this._updateStateFromProps(this.props);
    } else {
      this._updateValueFromState(this.state);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.value !== this.props.value) {
      this._updateStateFromProps(this.props);
    }
  }

  render() {
    const { label, value, skinnyDisplay, labelClassName, limitOptions, iconColor, labelStyle } =
      this.props;
    const { interval, hh, mm, aa, dayOfWeek, dayOfMonth, timestamp, isTimeSpecified } = this.state;

    if (!interval) {
      return (
        <div data-testid={this.props['data-testid']}>
          <label className={labelClassName}>{label} ( Cron Expression )</label>
          <StyledInputBase
            fullWidth={true}
            margin="dense"
            inputProps={{
              autoComplete: 'new-password',
            }}
            disabled={true}
            value={value}
          />
        </div>
      );
    }

    const intervalNeedsN =
      interval === 'N Hours' || interval === 'N Days' || interval === 'N Months';
    const className = classnames('frequency-field', { skinny: skinnyDisplay });
    let intervals = FREQUENCY_INTERVALS;

    if (limitOptions !== null) {
      intervals = filter(intervals, (i) => indexOf(limitOptions, i.value) !== -1);
    }

    return (
      <div data-testid={this.props['data-testid']} className={className}>
        <div className="frequency-types">
          <label className={labelClassName}>{label}&nbsp;</label>
          {this._renderSelectField('interval', intervals, 120)}
          {intervalNeedsN && <label>&nbsp;Every&nbsp;</label>}
          {intervalNeedsN &&
            this._renderSelectField(
              'n',
              interval === 'N Hours'
                ? N_HOURS_OPTIONS
                : interval === 'N Days'
                  ? N_DAYS_OPTIONS
                  : N_MONTHS_OPTIONS,
              50,
            )}
          {intervalNeedsN && <label>&nbsp;{interval.slice(interval.lastIndexOf(' ') + 1)}</label>}
          <div className="set-time">
            <StyledCheckbox
              checked={isTimeSpecified || false}
              onChange={this._handleSetTimeCheckboxChanged}
            />
            <label style={labelStyle}>{isTimeSpecified ? 'at' : 'Set Time'}&nbsp;</label>
          </div>
        </div>
        {isTimeSpecified &&
          ((interval === 'Once' && (
            <div className="poll-time">
              <DateTimeField value={timestamp} onChange={this._handleTimestampChanged} />
              <label>&nbsp;&nbsp;{getLocalTimeZoneLabel()}</label>
            </div>
          )) || (
            <TimeSelector
              interval={interval}
              hh={hh}
              mm={mm}
              aa={aa}
              dayOfWeek={dayOfWeek}
              dayOfMonth={dayOfMonth}
              onChange={this._handleInternalValueChanged}
            />
          ))}
      </div>
    );
  }

  _renderSelectField(name, options, width = 100) {
    const selectionValue = getCurrentSelectedOption(options, this.state[name]);
    const styles = {
      ...baseSelectStyles,
      control: (base) => base,
      container: (base) => ({ ...base, minWidth: width }),
      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
    };

    return (
      <Select
        data-testid={this.props['data-testid']}
        value={selectionValue}
        className={'field-frequency-select'}
        styles={styles}
        options={options}
        components={{ Input: SelectInput, Option: SelectOption }}
        isClearable={false}
        backspaceRemovesValue={false}
        isSearchable={true}
        onChange={(value) => this._handleInternalValueChanged(name, value)}
      />
    );
  }

  _handleInternalValueChanged(name, newValueObj) {
    let newValue = getNewSelectionValue(newValueObj, true);
    this._updateValueFromState({
      ...this.state,
      [name]: newValue,
    });
  }

  _handleTimestampChanged(timestamp) {
    this.setState(
      {
        timestamp,
      },
      () => {
        this._updateValueFromState(this.state);
      },
    );
  }

  _handleSetTimeCheckboxChanged(event, isChecked) {
    this.setState(
      {
        isTimeSpecified: isChecked,
      },
      () => {
        this._updateValueFromState(this.state);
      },
    );
  }

  _updateValueFromState(state) {
    const { onChange, name } = this.props;
    const { interval, isTimeSpecified } = state;
    const currentTimeState = this._getStateFromCurrentTime(interval);
    const sourceState = isTimeSpecified ? state : currentTimeState;
    const hh = sourceState.hh || currentTimeState.hh;
    const mm = sourceState.mm || currentTimeState.mm;
    const aa = sourceState.aa || currentTimeState.aa;
    const dayOfWeek = sourceState.dayOfWeek || currentTimeState.dayOfWeek;
    const dayOfMonth = sourceState.dayOfMonth || currentTimeState.dayOfMonth;
    const timestamp = sourceState.timestamp || currentTimeState.timestamp;
    const n = state.n;
    const newValue = UIUtils.getCron({
      interval,
      hh,
      mm,
      aa,
      dayOfWeek,
      dayOfMonth,
      timestamp,
      n,
    });

    onChange(newValue, name);
  }

  _updateStateFromProps(props) {
    const { value } = props;

    if (!value) {
      return;
    }

    const { interval, hh, mm, aa, dayOfWeek, dayOfMonth, timestamp, n } = UIUtils.parseCron(value);

    this.setState({
      interval,
      hh,
      mm,
      aa,
      dayOfWeek,
      dayOfMonth,
      timestamp,
      n,
    });
  }

  _getStateFromCurrentTime(interval) {
    return UIUtils.getCurrentDateAndTime(interval);
  }
}

FrequencyField.defaultProps = {
  skinnyDisplay: false,
  limitOptions: null,
  iconColor: '#ff7436',
  labelStyle: { fontWeight: 'normal' },
};

export default FrequencyField;
